import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { usd } from "@/lib/format";
import { cn } from "@/lib/utils";
import ProductLookupLoader from "@/components/analyzer/ProductLookupLoader";
import {
  RefreshCw,
  ScanSearch,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  Check,
} from "lucide-react";
import { toast } from "sonner";

const STAGE_COPY = {
  queued: "Waiting to start…",
  collecting: "Collecting public product details…",
  matching: "Searching Walmart candidates…",
  verifying: "Verifying Walmart prices…",
  completed: "Scoring complete",
  partial: "Finished with some source errors",
  failed: "Job failed",
};

function dash(v) {
  if (v === null || v === undefined || v === "") return "—";
  return v;
}

function money(v) {
  if (typeof v !== "number" || Number.isNaN(v)) return "—";
  return usd(v);
}

function MatchBadge({ status, availability }) {
  const avail = String(availability || "").toLowerCase();
  if (avail.includes("out of stock") || avail.replace(/\s|_/g, "").includes("outofstock")) {
    return <span className="ez-ts-badge ez-ts-badge-oos">Out of stock</span>;
  }
  if (avail.includes("unavailable")) {
    return <span className="ez-ts-badge ez-ts-badge-unavail">Unavailable</span>;
  }
  if (status === "exact") return <span className="ez-ts-badge ez-ts-badge-exact">Exact match</span>;
  if (status === "probable") return <span className="ez-ts-badge ez-ts-badge-probable">Probable match</span>;
  if (status === "review") return <span className="ez-ts-badge ez-ts-badge-review">Needs review</span>;
  return <span className="ez-ts-badge">—</span>;
}

function EvidenceDrawer({ evidence }) {
  const [open, setOpen] = useState(false);
  const rows = Array.isArray(evidence) ? evidence : [];
  if (!rows.length) return null;
  return (
    <div className="ez-ts-evidence">
      <button
        type="button"
        className="ez-ts-evidence-toggle"
        onClick={() => setOpen((o) => !o)}
        data-testid="opportunity-evidence-toggle"
      >
        Why this matched
        {open ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
      </button>
      {open && (
        <ul className="ez-ts-evidence-list" data-testid="opportunity-evidence">
          {rows.map((e) => (
            <li key={e.code || e.label}>
              <Check className="h-3 w-3 text-emerald-600" aria-hidden />
              {e.label || e.code}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function OpportunityCard({ opp, onReview, onAnalyze }) {
  const src = opp.source || {};
  const wm = opp.walmart || {};
  const match = opp.match || {};
  const spread = opp.price_spread;
  const status = match.status;
  const sourceReviewReasons = opp.source_review_reasons || [];
  const accepted = opp.review_status === "accepted";
  const canAnalyze =
    Boolean(wm.item_id) &&
    status !== "rejected" &&
    (sourceReviewReasons.length === 0 || accepted) &&
    (
      status === "exact" ||
      status === "probable" ||
      (status === "review" && accepted) ||
      (sourceReviewReasons.length > 0 && accepted)
    );

  const needsReview = (status === "review" || sourceReviewReasons.length > 0) && !accepted;

  return (
    <article className="ez-ts-card" data-testid={`opportunity-card-${opp.id}`}>
      <div className="ez-ts-card-top">
        <div className="ez-ts-images">
          <div className="ez-ts-img-wrap">
            {src.image ? (
              <img src={src.image} alt="" className="ez-ts-img" />
            ) : (
              <div className="ez-ts-img ez-ts-img-empty">Source</div>
            )}
            <span className="ez-ts-img-label">{dash(src.source_retailer_name || src.source_retailer)}</span>
          </div>
          <div className="ez-ts-img-wrap">
            {wm.image ? (
              <img src={wm.image} alt="" className="ez-ts-img" />
            ) : (
              <div className="ez-ts-img ez-ts-img-empty">Walmart</div>
            )}
            <span className="ez-ts-img-label">Walmart</span>
          </div>
        </div>
        <MatchBadge status={status} availability={wm.availability} />
      </div>

      <h3 className="ez-ts-title">{dash(wm.title || src.title)}</h3>

      <div className="ez-ts-prices">
        <div>
          <span className="ez-ts-k">Source</span>
          <b>{money(src.price)}</b>
        </div>
        <div>
          <span className="ez-ts-k">Walmart</span>
          <b>{money(wm.price)}</b>
        </div>
        <div>
          <span className="ez-ts-k">Price spread</span>
          <b className={cn(typeof spread?.absolute_spread === "number" && spread.absolute_spread > 0 && "text-emerald-700")}>
            {status !== "review" && status !== "rejected" && typeof spread?.absolute_spread === "number"
              ? money(spread.absolute_spread)
              : "—"}
          </b>
          {status !== "review" && status !== "rejected" && typeof spread?.spread_percent === "number" ? (
            <small>{spread.spread_percent.toFixed(1)}%</small>
          ) : null}
        </div>
      </div>

      <div className="ez-ts-meta">
        <span>Confidence {dash(match.confidence)}{typeof match.confidence === "number" ? "%" : ""}</span>
        {wm.seller ? <span>Seller {wm.seller}</span> : null}
        <span>Availability {dash(wm.availability)}</span>
        <span>Source ID {dash(src.source_product_id)}</span>
        <span className="ident">WM {dash(wm.item_id)}</span>
        <span>Source @ {dash(src.collected_at)}</span>
        <span>Walmart @ {dash(wm.checked_at)}</span>
      </div>

      {spread && typeof spread.absolute_spread === "number" ? (
        <p className="ez-ts-disclaimer">
          Price spread is not profit. Taxes, shipping, fees, prep, and availability may change the opportunity.
        </p>
      ) : (
        <p className="ez-ts-disclaimer">Price spread unavailable until the match is accepted with compatible live prices.</p>
      )}

      <EvidenceDrawer evidence={match.evidence} />

      {sourceReviewReasons.length > 0 && (
        <p className="ez-ts-disclaimer" data-testid={`opportunity-source-review-${opp.id}`}>
          Source needs review: {sourceReviewReasons.join(", ")}
        </p>
      )}

      <div className="ez-ts-actions">
        {needsReview && (
          <>
            <button
              type="button"
              className="ez-ts-btn primary"
              data-testid={`opportunity-accept-${opp.id}`}
              onClick={() => onReview(opp.id, "accepted")}
            >
              Accept match
            </button>
            <button
              type="button"
              className="ez-ts-btn"
              onClick={() => onReview(opp.id, "rejected")}
            >
              Reject
            </button>
          </>
        )}
        <button
          type="button"
          className="ez-ts-btn analyze"
          disabled={!canAnalyze}
          title={needsReview ? "Accept the match before analyzing" : "Open in Product Analyzer"}
          data-testid={`opportunity-analyze-${opp.id}`}
          onClick={() => canAnalyze && onAnalyze(wm.item_id)}
        >
          <ScanSearch className="h-4 w-4" /> Analyze
        </button>
      </div>
    </article>
  );
}

/**
 * Top-Selling Products — live sourcing opportunities only (no demo fixtures).
 */
export default function TopSellingOpportunities() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [job, setJob] = useState(null);
  const [jobBusy, setJobBusy] = useState(false);
  const [error, setError] = useState(null);
  const pollRef = useRef(0);
  const activeJobRef = useRef(null);

  const loadOpportunities = useCallback(async () => {
    try {
      const res = await api.productSourcingOpportunities({
        sort: "spread_desc",
        limit: 48,
      });
      const data = res?.data || {};
      if (data.demo_fixtures) {
        setItems([]);
        setError("Live endpoint returned an unexpected demo flag.");
        return;
      }
      setItems(Array.isArray(data.items) ? data.items : []);
      setError(null);
    } catch (err) {
      setItems([]);
      setError(
        err?.response?.data?.detail
          || err?.message
          || "Could not load opportunities",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOpportunities();
  }, [loadOpportunities]);

  const showLoader =
    jobBusy ||
    Boolean(
      job &&
        !["completed", "partial", "failed"].includes(job.status),
    );

  useEffect(() => {
    const terminal = ["completed", "partial", "failed"].includes(job?.status);
    if (!job?.job_id || terminal) return undefined;

    const jobId = job.job_id;
    activeJobRef.current = jobId;
    let delay = 1200;
    let cancelled = false;
    let timer;

    const tick = async () => {
      if (cancelled) return;
      const gen = ++pollRef.current;
      try {
        const res = await api.productSourcingJob(jobId);
        if (activeJobRef.current !== jobId) return;
        if (gen < pollRef.current - 5) return;
        const next = res?.data;
        if (next) setJob(next);
        if (["completed", "partial", "failed"].includes(next?.status)) {
          setJobBusy(false);
          await loadOpportunities();
          return;
        }
      } catch {
        /* keep polling */
      }
      delay = Math.min(8000, Math.round(delay * 1.35));
      if (!cancelled) timer = window.setTimeout(tick, delay);
    };
    timer = window.setTimeout(tick, delay);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [job?.job_id, job?.status, loadOpportunities]);

  const startLiveFeed = async () => {
    setJobBusy(true);
    setError(null);
    try {
      const res = await api.productSourcingCreateJob({
        max_products_per_source: 20,
        match_threshold: 85,
      });
      const created = res?.data;
      if (!created?.job_id) {
        throw new Error("Job was not created");
      }
      activeJobRef.current = created.job_id;
      setJob(created);
      toast.success("Live feed job started");
    } catch (err) {
      setJobBusy(false);
      const msg =
        typeof err?.response?.data?.detail === "string"
          ? err.response.data.detail
          : err?.message || "Could not start sourcing job";
      setError(msg);
      toast.error(msg);
    }
  };

  const onReview = async (id, review_status) => {
    try {
      await api.productSourcingReview(id, review_status);
      toast.success(review_status === "accepted" ? "Match accepted" : "Match rejected");
      await loadOpportunities();
    } catch (err) {
      toast.error(err?.message || "Review failed");
    }
  };

  const onAnalyze = (itemId) => {
    nav(`/app/analyzer?itemId=${encodeURIComponent(itemId)}`);
  };

  const stageLabel = (() => {
    if (!job) return null;
    const src = job.current_source ? `Scanning ${job.current_source}…` : null;
    if (job.current_stage === "collecting") return src || STAGE_COPY.collecting;
    if (job.current_stage === "matching") return STAGE_COPY.matching;
    if (job.current_stage === "verifying") return STAGE_COPY.verifying;
    return STAGE_COPY[job.status] || STAGE_COPY[job.current_stage] || "Working…";
  })();

  return (
    <div className="ez-ts" data-testid="top-selling-opportunities">
      <div className="ez-ts-toolbar">
        <div>
          <h1 className="ez-ts-heading">Top-Selling Products</h1>
          <p className="ez-ts-sub">
            Live source discoveries matched to verified Walmart prices. No demo fixtures.
          </p>
        </div>
        <button
          type="button"
          className="ez-ts-live"
          data-testid="top-selling-live-feed"
          onClick={startLiveFeed}
          disabled={showLoader}
        >
          <RefreshCw className={cn("h-4 w-4", showLoader && "animate-spin")} />
          {showLoader ? "Live feed running…" : "Live feed"}
        </button>
      </div>

      {showLoader && (
        <div className="ez-ts-job" data-testid="top-selling-job-progress">
          <ProductLookupLoader live />
          <div className="ez-ts-job-copy">
            <p className="ez-ts-job-stage">{stageLabel}</p>
            <ul>
              <li>Sources {dash(job?.sources_completed)} / {dash(job?.sources_total)}</li>
              <li>Collected {dash(job?.products_collected)}</li>
              <li>Matched {dash(job?.products_matched)}</li>
              <li>Needs review {dash(job?.products_review)}</li>
              <li>Rejected {dash(job?.products_rejected)}</li>
            </ul>
            {(job?.errors || []).length > 0 && (
              <div className="ez-ts-job-errors">
                <AlertTriangle className="h-4 w-4" />
                {(job.errors || []).slice(0, 5).map((e, i) => (
                  <span key={i}>
                    {e.source ? `${e.source}: ` : ""}
                    {e.error || "error"}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {error && !showLoader && (
        <div className="ez-ts-error" data-testid="top-selling-error" role="alert">
          <AlertTriangle className="h-4 w-4" />
          <span>{String(error)}</span>
          <button type="button" className="ez-ts-btn" data-testid="top-selling-retry" onClick={startLiveFeed}>
            Retry Live feed
          </button>
        </div>
      )}

      {job && ["partial", "failed", "completed"].includes(job.status) && !showLoader && (job.errors || []).length > 0 && (
        <div className="ez-ts-error" data-testid="top-selling-job-errors" role="status">
          <AlertTriangle className="h-4 w-4" />
          {(job.errors || []).slice(0, 5).map((e, i) => (
            <span key={i}>{e.source ? `${e.source}: ` : ""}{e.error || "error"}</span>
          ))}
          {job.status === "partial" || job.status === "failed" ? (
            <button type="button" className="ez-ts-btn" onClick={startLiveFeed}>Retry</button>
          ) : null}
        </div>
      )}

      {loading && !showLoader ? (
        <p className="ez-ts-empty">Loading opportunities…</p>
      ) : !items.length && !showLoader ? (
        <div className="ez-ts-empty" data-testid="top-selling-empty">
          <p>No live opportunities yet.</p>
          <p>Start a Live feed scan to collect allowlisted retailer products and verify Walmart matches.</p>
          <button type="button" className="ez-ts-live" style={{ marginTop: 12 }} onClick={startLiveFeed}>
            <RefreshCw className="h-4 w-4" /> Start Live feed
          </button>
        </div>
      ) : (
        <div className="ez-ts-grid" data-testid="top-selling-grid">
          {items
            .filter((o) => (o.match || {}).status !== "rejected")
            .map((opp) => (
              <OpportunityCard
                key={opp.id || opp.opportunity_key}
                opp={opp}
                onReview={onReview}
                onAnalyze={onAnalyze}
              />
            ))}
        </div>
      )}

      <p className="ez-ts-foot">
        Prefer a single product?{" "}
        <Link to="/app/analyzer" className="text-emerald-700 font-semibold">
          Open Product Analyzer
        </Link>
      </p>
    </div>
  );
}
